import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import { PERSONALIZATION_TOP_SECTION } from 'bundles/personalization-settings/constants';
import { useIsPersonalizationSettingsEnabled } from 'bundles/personalization-settings/featureFlags';

/**
 * Destination for the site-wide "Edit goal" / edit-profile entry points.
 *
 * Historically these CTAs deep-link into the onboarding stepper (via
 * {@link useBuildOnboardingUrl}). When the `personalised_settings_page` Statsig
 * gate is on, we instead route the learner to the new Personalization tab on
 * Account Settings, where they can edit the same Learner Object attributes
 * (goal, role, skills, education) without re-running onboarding (LO-4742).
 *
 * The gate defaults OFF, so this preserves today's onboarding behavior until
 * rollout. `useGateValue` returns false (it does not throw) when no
 * StatsigProvider is present, so surfaces without a provider also fall back to
 * the onboarding URL.
 *
 * Takes the same arguments as {@link useBuildOnboardingUrl} and returns the same
 * `{ url, loading }` shape, so call sites swap the hook in place.
 */
const PERSONALIZATION_SETTINGS_URL = `/account-settings#${PERSONALIZATION_TOP_SECTION}`;

const useEditGoalUrl = (args: Parameters<typeof useBuildOnboardingUrl>[0]): { url: string; loading: boolean } => {
  const isPersonalizationEnabled = useIsPersonalizationSettingsEnabled();
  // Always call the onboarding builder so hook order is stable and we keep the
  // exact gate-off behavior; only the destination changes when the gate is on.
  const { onboardingUrl, loading } = useBuildOnboardingUrl(args);

  if (isPersonalizationEnabled) {
    return { url: PERSONALIZATION_SETTINGS_URL, loading: false };
  }

  return { url: onboardingUrl, loading };
};

export default useEditGoalUrl;
